import { FC } from "react";
import { Trans, t } from "@lingui/macro";
import styled from "@emotion/styled";
import SearchDesktopView from "./components/SearchDesktopView";
import SearchMobileView from "./components/SearchMobileView";
import { localeFix } from "src/utils/genderLocale/genderLocale";
import { PageHead } from "src/molecules/PageHead";
import { getDeepLink } from "src/utils/getDeepLink";
import { useLingui } from "@lingui/react";
import { Locale } from "src/lib/Intl";
import type { AllBrandData } from "src/types/adapters/browse.type";
import type { SeoQueryResponse } from "src/types/graphql.d";

const Title = styled.h1`
  color: ${({ theme }) => theme.colors.tone.black};
  font-size: 32px;
  line-height: 1.25;
  margin: 32px auto;
  max-width: 1264px;
  padding: 0 20px;
  text-transform: uppercase;

  @media (max-width: ${({ theme }) => theme.breakpoints[1]}) {
    font-size: 24px;
    line-height: 1.33;
    margin: 16px auto;
    text-align: center;
  }
`;

interface Props {
  genderId?: string;
  locale: string;
  brands: AllBrandData[];
  seoContent?: SeoQueryResponse | null;
}

const AllBrandsSearch: FC<Props> = ({
  genderId,
  locale,
  brands,
  seoContent,
}) => {
  // If genderId is present, we normalize it. If not, we keep it undefined to generate genderless links.
  const linkGender = genderId ? localeFix({ gender: genderId }) : undefined;

  const { i18n } = useLingui();
  const description = genderId
    ? t(
        i18n
      )`Discover all the brands for {genderId} at Otrium. Shop top-brand clothing with discounts up to 70%. Find your favorite brands at great prices!`
    : t(
        i18n
      )`Discover all the brands at Otrium. Shop top-brand clothing with discounts up to 70%. Find your favorite brands at great prices!`;

  const deepLink = getDeepLink({
    route: ["brands", linkGender],
  });

  return (
    <main>
      <PageHead
        title={t(i18n)`Brands A to Z`}
        description={description}
        deeplink={deepLink}
        enableIndexation
        seoContent={seoContent}
      />
      <header>
        <Title>
          <Trans>Brands A to Z</Trans>
        </Title>
      </header>
      <SearchDesktopView
        brands={brands}
        genderId={linkGender}
        locale={locale as Locale}
      />
      <SearchMobileView
        brands={brands}
        genderId={linkGender}
        locale={locale as Locale}
      />
    </main>
  );
};

export default AllBrandsSearch;
